import { useState } from "react";
import { Grid2, IconButton, TextField, Typography } from "@mui/material";
import { Check, Close, Edit } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { BookingType } from "../../../../../features/Booking-Form/typings/booking-inputs";
import type { BookingScheduleInterface } from "../../../../../features/Booking-Form/typings/booking.types";
import {
  buildResourceBookingQtyUpdateDto,
  resolveResourceBookingOrderedQty
} from "../../../../../features/Schedule/utils/resource-booking-schedule.utils";
import {
  isResourceCateringQuantityValid,
  MAX_RESOURCE_CATERING_ORDER_QTY,
  parseResourceCateringQuantity
} from "../../../../BookingForm/partial/resource-catering-quantity-field.partial";
import { useRemoteUpdateBookingServicesByUser } from "../../../../../hooks/Remote/Services/OrderRequests/useRemoteUpdateBookingServicesByUser";
import { ScheduleResourceBookingMetaField } from "./ScheduleResourceBookingMetaField.partial";

type ScheduleResourceBookingQtyProps = Readonly<{
  schedule: BookingScheduleInterface;
  isEditable: boolean;
  refetchAllSchedule?: () => void;
}>;

const qtyFieldSx = {
  width: 72,
  "& input[type=number]": {
    textAlign: "center",
    MozAppearance: "textfield",
    padding: "4px"
  },
  "& input[type=number]::-webkit-outer-spin-button": { WebkitAppearance: "none", margin: 0 },
  "& input[type=number]::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 }
} as const;

const qtyLabelSx = {
  fontWeight: "bold",
  color: "text.primary",
  mr: 0.5
} as const;

export function ScheduleResourceBookingQty({
  schedule,
  isEditable,
  refetchAllSchedule
}: ScheduleResourceBookingQtyProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: updateBookingServices } = useRemoteUpdateBookingServicesByUser();

  const initialQty = resolveResourceBookingOrderedQty(schedule);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(initialQty));

  const canEdit =
    isEditable &&
    schedule.bookingType === BookingType.RESOURCE &&
    schedule.serviceBookings?.[0] != null &&
    refetchAllSchedule != null;

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(String(initialQty));
  };

  const handleSave = async () => {
    if (!canEdit) return;

    const parsed = parseResourceCateringQuantity(editValue);
    if (
      !isResourceCateringQuantityValid(parsed, MAX_RESOURCE_CATERING_ORDER_QTY) ||
      parsed == null
    ) {
      return;
    }

    if (parsed === initialQty) {
      setIsEditing(false);
      return;
    }

    const payload = buildResourceBookingQtyUpdateDto(schedule, parsed);
    if (payload == null) {
      enqueueSnackbar(t("_scheduleSelection_resourceQtyEditError"), { variant: "error" });
      return;
    }

    try {
      await updateBookingServices([payload]);
      refetchAllSchedule?.();
      enqueueSnackbar(t("Successfully edited booking"), { variant: "success" });
      setIsEditing(false);
    } catch {
      enqueueSnackbar(t("_scheduleSelection_resourceQtyEditError"), { variant: "error" });
    }
  };

  if (isEditing) {
    const parsed = parseResourceCateringQuantity(editValue);
    const showError =
      editValue.trim() !== "" &&
      !isResourceCateringQuantityValid(parsed, MAX_RESOURCE_CATERING_ORDER_QTY);

    return (
      <Grid2
        container
        alignItems="center"
        gap={0.5}
        data-testid="schedule-resource-booking-qty"
        sx={{ width: "100%", pl: 0, flexWrap: "nowrap" }}
      >
        <Typography component="span" sx={qtyLabelSx}>
          {t("Qty")}
        </Typography>
        <TextField
          type="number"
          size="small"
          value={editValue}
          onChange={event => setEditValue(event.target.value)}
          error={showError}
          sx={qtyFieldSx}
          slotProps={{
            htmlInput: {
              min: 1,
              max: MAX_RESOURCE_CATERING_ORDER_QTY,
              "data-testid": "schedule-resource-booking-qty-input"
            }
          }}
        />
        <IconButton
          data-testid="schedule-resource-booking-qty-save"
          onClick={handleSave}
          size="small"
          aria-label={t("Save")}
        >
          <Check fontSize="small" />
        </IconButton>
        <IconButton
          data-testid="schedule-resource-booking-qty-cancel"
          onClick={handleCancel}
          size="small"
          aria-label={t("Cancel")}
        >
          <Close fontSize="small" />
        </IconButton>
      </Grid2>
    );
  }

  return (
    <ScheduleResourceBookingMetaField
      label={t("Qty")}
      testId="schedule-resource-booking-qty"
      endAdornment={
        canEdit ? (
          <IconButton
            data-testid="schedule-resource-booking-qty-edit"
            onClick={() => {
              setEditValue(String(initialQty));
              setIsEditing(true);
            }}
            size="small"
            sx={{ padding: 0 }}
            aria-label={t("Edit")}
          >
            <Edit fontSize="small" />
          </IconButton>
        ) : undefined
      }
    >
      {initialQty}
    </ScheduleResourceBookingMetaField>
  );
}
